import { cloneDeep, has, hasIn, isArray, isEmpty, isNull, isObject, isUndefined } from 'lodash-es'
import { useEvent, useProject } from '@haohan/clw-store'
import { toRaw } from 'vue'
import {
  getRouterPushUrl,
  dialogEvent,
  transformInvokePayloadToFormDataFromKeys,
  handleApiUploadFile,
  handleApiAfterFun,
  handleApiResponseData,
  transformLogicFlowParams,
  isValidKey,
  emitter,
  findNodeByKey
} from '@haohan/clw-utils'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getApiMenu, invokeApiMenu, invokeLogicFlow, queryLFMenuItemReq } from '@haohan/clw-api'
import { eventCenter } from '../event-center/index'
import type { ComponentOperationData, ComponentRegisterInfo } from '../types/index'
import { useRouter } from 'vue-router'

export const updateMutualPool = (
  mutualPool: MutualPool,
  sendKey: string,
  value: any,
  status: MutualPoolDataType = 'ok'
) => {
  if (mutualPool[sendKey]) {
    mutualPool[sendKey].status = status
    mutualPool[sendKey].value = value
    mutualPool[sendKey].updateTime = new Date().getTime()
  }
}
/**
 * 根据所有接收事件，返回对应交互值
 * @param mutualPool 数据池
 * @param receiveEvents 接收方法
 * @returns 交互值
 */
export const getEventValues = (
  mutualPool: MutualPool,
  receiveEvents: WidgetEvent[]
): MutualQueryEventValueData => {
  const eventValue: MutualQueryEventValueData = {}
  const eventStore = useEvent()
  // 使用 toRaw 将一个由reactive生成的响应式对象转为普通对象 避免响应式监听导致的重复请求
  const pageParams = toRaw(eventStore.pageParams)
  receiveEvents.forEach((e: WidgetEvent) => {
    if (e.behavior === 'runIn') {
      if (has(pageParams, e.receiveEventKey!)) {
        // TODO: 需要处理重复过来字段，比如文本组件监听跳转得值，本身又监听图点击发送，都是过滤同个字段
        const _pageParam = pageParams[e.receiveEventKey!]
        eventValue[e.receiveEventKey!] = {
          value: _pageParam.value,
          filterProp: e.filterProp || '',
          filterOperatorType: e.filterOperatorType || '',
          filterValueType: e.filterValueType || '',
          filterInsertCol: e.filterInsertCol,
          filterInsertColType: e.filterInsertColType,
          filterInsertValueType: e.filterInsertValueType,
          type: 'runIn',
          time: new Date().getTime()
        }
      }
    } else {
      // 获得交互池中存储得对应发送事件对象
      const _key = getEventKey(e.receiveEventKey!, e.currentPageId)
      const mutualPoolData = mutualPool[_key]
      if (mutualPoolData && mutualPoolData.value !== '') {
        // 当交互类型为表单过滤并且绑定得值类型为form时，才会遍历添加
        if (e.behavior === 'formFilter' && e.bindValueType === 'form') {
          Object.keys(mutualPoolData.value).forEach((key: string) => {
            if (
              !isNull(mutualPoolData.value[key].value) &&
              !isUndefined(mutualPoolData.value[key].value) &&
              mutualPoolData.value[key].value !== '' &&
              !mutualPoolData.value[key].isIgnore
            ) {
              eventValue[`${e.receiveEventKey!}_${key}`] = {
                value: mutualPoolData.value[key].value,
                filterProp: key,
                filterOperatorType: mutualPoolData.value[key].filterOperatorType,
                filterValueType: mutualPoolData.value[key].filterValueType || '',
                filterInsertCol: mutualPoolData.value[key].isFilterInsertCol
                  ? key
                  : e.filterInsertCol,
                filterInsertColType: mutualPoolData.value[key].filterInsertColType,
                filterInsertValueType: mutualPoolData.value[key].filterInsertValueType,
                time: mutualPoolData.updateTime
              }
            }
          })
        } else {
          eventValue[e.receiveEventKey!] = {
            value: mutualPoolData.value,
            filterProp: e.filterProp || '',
            filterOperatorType: e.filterOperatorType || '',
            filterValueType: e.filterValueType || '',
            filterInsertCol: e.filterInsertCol,
            filterInsertColType: e.filterInsertColType,
            filterInsertValueType: e.filterInsertValueType,
            time: mutualPoolData.updateTime
          }
        }
      }
    }
  })
  return filterCommonEventValues(eventValue)
}

/**
 * 为了避免多个交互过滤同个值，导致过滤值错误问题
 * 这里进行一个去重操作，只保留时间最新的过滤值
 * @param eventValues 交互事件值
 * @returns 实际事件值
 */
const filterCommonEventValues = (eventValues: MutualQueryEventValueData) => {
  // key filterInsertCol+filterInsertColType+filterInsertValueType
  // value: {eventVKey: x, time: x}
  const _ev: any = {}
  Object.keys(eventValues).forEach((key: string) => {
    let _k = `${eventValues[key].filterProp}${eventValues[key].filterOperatorType}${eventValues[key].filterValueType}`
    if (has(eventValues[key], 'filterInsertCol') && eventValues[key].filterInsertCol !== '') {
      _k = `${eventValues[key].filterInsertCol}${eventValues[key].filterInsertColType}${eventValues[key].filterInsertValueType}`
    }

    if (has(_ev, _k)) {
      // 对比更新时间，只保留最新的
      if (_ev[_k].time < eventValues[key].time) {
        _ev[_k] = {
          eventVKey: key,
          time: eventValues[key].time
        }
      }
    } else {
      _ev[_k] = {
        eventVKey: key,
        time: eventValues[key].time
      }
    }
  })
  // 只返回需要留下的
  const _eventValues: MutualQueryEventValueData = {}
  Object.keys(_ev).forEach((key: string) => {
    _eventValues[_ev[key].eventVKey] = eventValues[_ev[key].eventVKey]
  })
  return _eventValues
}

/**
 * 根据绑定值类型转换值为对应类型
 * @param valueType 绑定值类型
 * @param value 值
 * @returns
 */
export const getEventValueByValueType = (valueType: string, value: any) => {
  // 如果设置了交互的值类型，则要进行特殊处理
  let _value = value
  if (!valueType || valueType === '' || value === '') {
    return _value
  } else if (valueType === 'string') {
    if (isArray(_value)) {
      _value = _value.join(',')
    } else if (isObject(_value)) {
      _value = JSON.stringify(_value)
    }
    return _value
  } else if (valueType === 'array') {
    _value = isArray(_value) ? _value : [_value]
    return _value
  }
  return _value
}
/**
 * 处理交互绑定的高级处理事件
 * @param highLevelFun 绑定的高级处理事件
 * @param val 交互值
 */
export const handleHighLevelFun = (highLevelFun: string, val: any) => {
  if (highLevelFun === 'splitBindGloablFun') {
    splitBindGlobalFun(val)
  }
}

/**
 * 切割值，根据指定规则更新公共参数中对应得值
 * @param val 指定得值
 */
const splitBindGlobalFun = (val: string) => {
  if (val !== '') {
    const eventStore = useEvent()
    const _vals = val.split(',')
    _vals.forEach((_val: string) => {
      // const _vs = _val.split(':')
      // if (_vs.length === 2) {
      //   eventStore.updateCommonParametersByLabel(_vs[0], _vs[1])
      // }
      const ix = _val.indexOf(':')
      if (ix > -1) {
        eventStore.updateCommonParametersByLabel(_val.slice(0, ix), _val.slice(ix + 1))
      }
    })
  }
}
/**
 * 处理跳转交互
 * @param eventStore Event store
 * @param projectStore project store
 * @param event 发送事件
 * @param val 事件值
 */
export const handleBehaviorByRunIn = (
  eventStore: any,
  projectStore: any,
  event: WidgetEvent,
  val: any
) => {
  // 获得当前页面ID
  const sourcePageId = projectStore.currentPage.id
  eventStore.changePageParams(event.key, {
    sourcePageId: sourcePageId,
    targetPageId: event.pageId,
    value: val
  })
  // 判断当前状态
  if (
    window.location.href.indexOf('preview') !== -1 ||
    window.location.href.indexOf('bigScreen') !== -1 ||
    window.location.href.indexOf('bigscreen') !== -1
  ) {
    const pageIds = event.pageId.split(',')
    if (!event.pageId.trim()) {
      console.log('不存在跳转页面')
      return
    }
    if (pageIds.length === 1) {
      const pnode = findNodeByKey(projectStore.pages, pageIds[0])
      if (pnode) {
        emitter.emit(
          'event-router-change',
          getRouterPushUrl({
            preFix: event?.pageType === 'bigScreen' ? 'bigscreen' : 'preview',
            pageId: event.pageId
          })
        )
      } else {
        ElMessage({
          message: '抱歉，您暂无权限浏览该页面',
          type: 'warning'
        })
      }
    } else {
      // TODO: 弹窗下钻选择
      // 如果绑定了多个页面，则弹出页面选择框
      eventStore.changeJumpPageIds(event.pageId)
      eventStore.changeJumpPageTypes(event.pageType)
      eventStore.changeShowMultipJumpType('runIn')
      eventStore.changeShowMultipJumpDialog(true)
    }
  } else {
    // TODO: 暂不支持在编辑模式下使用
  }
}
/**
 * 处理弹窗交互
 * @param eventStore Event store
 * @param projectStore project store
 * @param event 发送事件
 * @param val 事件值
 */
export const handleBehaviorByDialog = (
  eventStore: any,
  projectStore: any,
  event: WidgetEvent,
  val: any
) => {
  // TODO: 弹窗处理
  // 获得当前页面ID
  const sourcePageId = projectStore.currentPage.id
  eventStore.changePageParams(event.key, {
    sourcePageId: sourcePageId,
    targetPageId: event.pageId,
    value: val
  })
  // 处于预览或者发布状态
  const _pageIds = event.pageId.split(',')
  if (_pageIds.length === 1) {
    // 处于预览或者发布状态
    dialogEvent.emit(event.key, event.pageId)
  } else {
    // 如果绑定了多个页面，则弹出页面选择框
    eventStore.changeJumpPageIds(event.pageId)
    eventStore.changeShowMultipJumpType('dialog')
    eventStore.changeShowMultipJumpDialog(true)
  }
}
/**
 * 处理API交互
 * @param event 发送事件
 * @param val 事件值
 * @returns
 */
export const handleBehaviorByApi = (event: WidgetEvent, val: any) => {
  // 访问API处理
  if (event.apiParam && event.apiParam?.apiId) {
    // ((isString(val) && val.trim() === '') || (isArray(val) && val.length === 0) || isEmpty(val))
    if (
      event.apiParam?.emptyTip &&
      event.apiParam?.emptyTip !== '' &&
      (isEmpty(val) || val === '[]')
    ) {
      return ElMessage.warning(event.apiParam?.emptyTip)
    }
    // 提示
    if (event.apiParam?.tip !== '') {
      ElMessageBox.confirm(event.apiParam?.tip, '是否执行', {
        confirmButtonText: '执行',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          exeInvokeApiEvent(event, val)
        })
        .catch(() => {})
    } else {
      exeInvokeApiEvent(event, val)
    }
  }
}

/**
 * 执行接口调用
 */
const exeInvokeApiEvent = async (event: WidgetEvent, val: any) => {
  const projectStore = useProject()
  const projectId = projectStore.currentProject.id || ''
  const apiId = event.apiParam?.apiId || ''
  let param: any = {}
  if (event.apiParam!.bindApiParam) {
    try {
      param[event.apiParam!.bindApiParam] = val
    } catch (error) {
      // param[event.apiParam.bindApiParam] = val
    }
  } else {
    try {
      // 需要增加类型判断，如果是对象则不再执行转换
      if (val && typeof val === 'string') {
        param = JSON.parse(val)
      } else {
        param = val
      }
    } catch (error) {
      console.error('exeInvokeApiEvent 转换参数值错误', error)
    }
  }

  // 批量删除时, param 拿到的格式: {body: [{}, {}]}
  const { data } = await getApiMenu(projectId, apiId)
  const formData = transformInvokePayloadToFormDataFromKeys(data?.keysDefine.def, param)
  // 处理上传文件
  await handleApiUploadFile(data, formData)
  // let payload = {}
  // try {
  //   payload = JSON.parse(val)
  // } catch (error) {}
  // console.log(param, payload)
  invokeApiMenu(projectId, apiId, formData, data.headers).then((res: any) => {
    // console.log('res', res)
    res = handleApiAfterFun(data.postPosition, res)
    const _res = handleApiResponseData(res)
    if (
      res?.status === 200 &&
      has(res, 'data') &&
      has(res.data, 'status') &&
      res.data.status !== 200 &&
      res.data.status !== 0
    ) {
      const message = res?.data.message || res?.data.tag || '数据提交失败，请检查提交数据是否正确'
      ElMessage.error(message)
      return false
    }
    // 后端可能不返回内容, status 为 200 也表示成功, 即前端 {data: ''}
    if (_res?.status === 0 || _res?.status === 200 || (!_res?.status && !_res.data)) {
      console.log(' 执行成功 ', _res)
      eventCenter.emitEvent(event, val)
      ElMessage({
        type: 'success',
        message: '执行成功'
      })
    } else {
      const message = _res?.message || _res?.tag || '执行失败'
      ElMessage({
        type: 'error',
        message: message
      })
    }
  })
}
/**
 * 处理访问逻辑六交互
 * @param event 发送事件
 * @param val 事件值
 * @returns
 */
export const handleBehaviorByLogicFlow = (event: WidgetEvent, val: any) => {
  // 访问API处理
  if (event.apiParam && event.apiParam?.apiId) {
    // ((isString(val) && val.trim() === '') || (isArray(val) && val.length === 0) || isEmpty(val))
    if (
      event.apiParam?.emptyTip &&
      event.apiParam?.emptyTip !== '' &&
      (isEmpty(val) || val === '[]')
    ) {
      return ElMessage.warning(event.apiParam?.emptyTip)
    }
    // 提示
    if (event.apiParam?.tip !== '') {
      ElMessageBox.confirm(event.apiParam?.tip, '是否执行', {
        confirmButtonText: '执行',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          exeInvokeLogicFlowEvent(event, val)
        })
        .catch(() => {})
    } else {
      exeInvokeLogicFlowEvent(event, val)
    }
  }
}
/**
 * 执行逻辑流调用
 */
const exeInvokeLogicFlowEvent = async (event: WidgetEvent, val: any) => {
  const projectStore = useProject()
  const projectId = projectStore.currentProject.id || ''
  const apiId = event.apiParam?.apiId || ''
  let param: any = {}
  if (event.apiParam!.bindApiParam) {
    try {
      param[event.apiParam!.bindApiParam] = val
    } catch (error) {
      // param[event.apiParam.bindApiParam] = val
    }
  } else {
    try {
      param = JSON.parse(val)
    } catch (error) {}
  }

  // 批量删除时, param 拿到的格式: {body: [{}, {}]}
  const { data } = await queryLFMenuItemReq(projectId, apiId)
  invokeLogicFlow(projectId, apiId, transformLogicFlowParams(data.parameters, param))
    .then((res: any) => {
      // 后端可能不返回内容, status 为 200 也表示成功, 即前端 {data: ''}
      if (res?.status === 0 || res?.status === 200 || (!res?.status && !res.data)) {
        console.log(' 执行成功 ', res)
        eventCenter.emitEvent(event, val)
        ElMessage({
          type: 'success',
          message: '执行成功'
        })
      } else {
        const message = res?.message || res?.tag || '执行失败'
        ElMessage({
          type: 'error',
          message: message
        })
      }
    })
    .catch((error: any) => {
      console.error(' 执行失败 ', error)
      ElMessage({
        type: 'error',
        message: '执行失败'
      })
    })
}
/**
 * 处理访问外链事件
 * @param event 触发事件
 */
export const handleBehaviorByToOutUrl = (event: WidgetEvent) => {
  if (
    window.location.href.indexOf('preview') !== -1 ||
    window.location.href.indexOf('bigscreen') !== -1
  ) {
    // 访问外部链接
    if (event.openOutUrlType === 'currentPage') {
      window.location.href = event.outUrl || ''
    } else {
      window.open(event.outUrl || '', '_blank')
    }
  }
}

export const handleSendEventValue = (
  item: Widget,
  sendEvent: WidgetEvent,
  canSelectBindValues: {}
) => {
  const eventStore = useEvent()
  const _this: any = canSelectBindValues
  let val = ''
  const bindValue = sendEvent.bindValue!
  let oriBindValue, level: any
  if (item && item.type === 'HHSelectWidget' && bindValue.indexOf('-') > -1) {
    ;[oriBindValue, level] = bindValue.split('-')
  } else {
    oriBindValue = bindValue
  }
  if (isValidKey(oriBindValue, _this)) {
    if (level === undefined) {
      val = _this[oriBindValue]
    } else if (item.prop?.type === 'multiple') {
      val = _this[oriBindValue].map((el: any) => el[level])
    } else {
      const rt = _this[oriBindValue]
      val = Array.isArray(rt) ? rt[level] : ''
    }
    // val = level === undefined ? _this[oriBindValue] : _this[oriBindValue][level]
  } else if (hasIn(_this, 'eventData') && hasIn(_this.eventData, oriBindValue)) {
    val = _this.eventData[oriBindValue]
  } else if (hasIn(eventStore.pageWidgetParam, oriBindValue)) {
    val =
      eventStore.pageWidgetParam[oriBindValue].value === ''
        ? ''
        : JSON.stringify(eventStore.pageWidgetParam[oriBindValue].value)
  }
  return val
}
/**
 * 构建组件出方法外的其它信息
 * @param widget 组件
 * @returns 组件注册信息，处绑定操作方法外
 */
export const buildRegisterComponentInfo = (
  widget: Widget,
  onOperation: (operation: string, data: ComponentOperationData) => void,
  getEventValueCanUseObj: () => { [key: string]: any }
) => {
  const groupOp: Record<
    string,
    {
      widgetId: string
      currentPageId: string
      needJudgePromptly: boolean
      receiveEvent: WidgetEvent[]
    }
  > = {}
  const reactiveEvents =
    cloneDeep(widget.events?.filter((e: WidgetEvent) => e.eventType === 'receive')) || []

  reactiveEvents!.forEach((e: WidgetEvent) => {
    e.widgetKey = widget.key
    if (e.performOperation && e.performOperation !== '') {
      // 根据执行方法进行分组，并初始化
      if (!groupOp[e.performOperation!]) {
        groupOp[e.performOperation!] = {
          currentPageId: widget.currentPageId as string,
          widgetId: widget.key as string,
          needJudgePromptly: false,
          receiveEvent: []
        }
      }
      if (e.receiveEventKey && e.receiveEventKey !== '') {
        groupOp[e.performOperation!].receiveEvent.push(e)
      }
      // 判断是否具有需要接听立即执行状态的发送事件
      if (e.action === 'promptlyFilters') {
        groupOp[e.performOperation!].needJudgePromptly = true
      }
    }
  })
  return {
    id: getEventKey(widget.key!, widget.currentPageId),
    receiveEvents: reactiveEvents,
    groupOp,
    onOperation,
    getEventValueCanUseObj
  } as ComponentRegisterInfo
}
/**
 * 按固定规则获得唯一Key
 * @param key key
 * @param pageId 页面ID
 * @returns 唯一Key
 */
export const getEventKey = (key: string, pageId = '') => {
  return `${key}_${pageId}`
}
